import {
  persistQueryClientSubscribe,
  type AsyncStorage,
  type Persister,
  type PersistedClient,
} from '@tanstack/query-persist-client-core';
import {
  QueryClient,
  hydrate,
  type VueQueryPluginOptions,
  type Query,
} from '@tanstack/vue-query';
import { get, set, del, createStore, type UseStore } from 'idb-keyval';

function newIdbStorage(idbStore: UseStore): AsyncStorage {
  return {
    getItem: async (key) => {
      const v = await get<string>(key, idbStore);
      return v ?? null;
    },
    setItem: async (key, value) => {
      await set(key, value, idbStore);
    },
    removeItem: async (key) => {
      await del(key, idbStore);
    },
  };
}

const idbStore = createStore('query', 'client');
const storage = newIdbStorage(idbStore);
const PERSIST_KEY = 'tanstack-query-persist:client';

let persistenceRevision = 0;
let pendingWrite = Promise.resolve();
const enqueueWrite = (write: () => Promise<void>) => {
  pendingWrite = pendingWrite.catch(() => undefined).then(write);
  return pendingWrite;
};

const persister: Persister = {
  persistClient: async (client: PersistedClient) => {
    await enqueueWrite(async () => {
      await storage.setItem(PERSIST_KEY, JSON.stringify(client));
    });
  },
  restoreClient: async () => {
    const revision = persistenceRevision;
    const str = await storage.getItem(PERSIST_KEY);
    if (revision !== persistenceRevision || !str) return undefined;
    try {
      return JSON.parse(str) as PersistedClient;
    } catch {
      await enqueueWrite(async () => {
        await storage.removeItem(PERSIST_KEY);
      });
      return undefined;
    }
  },
  removeClient: async () => {
    await enqueueWrite(async () => {
      await storage.removeItem(PERSIST_KEY);
    });
  },
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const persistenceOptions = {
  queryClient,
  persister,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) =>
      query.meta?.persist !== false && query.state.status === 'success',
  },
};
let unsubscribe: (() => void) | undefined;
let disposed = false;

export const resetSessionQueries = async () => {
  const revision = ++persistenceRevision;
  unsubscribe?.();
  const cancellation = queryClient.cancelQueries();
  queryClient.clear();
  try {
    await cancellation;
    await persister.removeClient();
  } finally {
    if (
      !disposed &&
      revision === persistenceRevision &&
      typeof window !== 'undefined'
    ) {
      unsubscribe = persistQueryClientSubscribe(persistenceOptions);
    }
  }
};

if (typeof window !== 'undefined') {
  const revision = persistenceRevision;
  const restore = async () => {
    try {
      const persisted = await persister.restoreClient();
      if (disposed || revision !== persistenceRevision) return;
      // Outer safety bound only: how old a persisted snapshot we'll paint at all
      // before the corrective network refetch lands. It is not the correctness
      // mechanism — each query's own staleTime + refetchOnMount governs whether
      // that painted snapshot gets silently trusted instead of revalidated. Content
      // a BackOffice can change (branding, home presentation, FAQ, catalog views)
      // must always set refetchOnMount: 'always' and a short staleTime; see
      // shared/query/backofficeContentStaleTime.ts.
      const MAX_PERSISTED_SNAPSHOT_AGE_MS = 1000 * 60 * 60;
      if (
        persisted?.timestamp &&
        persisted.buster === '' &&
        Date.now() - persisted.timestamp <= MAX_PERSISTED_SNAPSHOT_AGE_MS
      ) {
        hydrate(queryClient, persisted.clientState);
      } else if (persisted) {
        await persister.removeClient();
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!disposed && revision === persistenceRevision) {
        unsubscribe = persistQueryClientSubscribe(persistenceOptions);
      }
    }
  };
  void restore();

  const onBeforeUnload = () => {
    disposed = true;
    unsubscribe?.();
  };
  window.addEventListener('beforeunload', onBeforeUnload);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      onBeforeUnload();
      window.removeEventListener('beforeunload', onBeforeUnload);
    });
  }
}

export const vueQueryOptions = {
  enableDevtoolsV6Plugin: true,
  queryClient: queryClient,
} satisfies VueQueryPluginOptions;
